import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Button from '../../../GbsUI/UI/Button';
import ArrowRightIcon from '../../../GbsUI/UI/icons/ArrowRight';
import Repeater from '../../../GbsUI/UI/Repeater';
import {useTaxonomy} from '../../../hooks/useTaxonomy';
import {action} from '../../Home/action';
import EducationItem from '../components/EducationItem';
import SignupLayout from '../components/Layout';
import {SignupContext} from '../context';
import stepTwoSchema from '../schema/step-two';

const SignupStepTwo = ({history}) => {
  const {state, setState} = useContext(SignupContext);
  const [formErrors, setFormErrors] = useState(new Map());
  const {data: degrees, isLoading: isDegreesLoading} = useTaxonomy('DEGREES');
  const {data: gpa, isLoading: isGpaLoading} = useTaxonomy('GPA');
  const initialized = useRef(false);

  const items = useMemo(() => {
    return state?.education?.map((item, index) => ({
      removable: index > 0,
      data: item,
    }));
  }, [state?.education]);

  const validate = useCallback((scrollToError = true, abortEarly = false) => {
    return stepTwoSchema.validate(state.education, { abortEarly }).then((data) => {
      setFormErrors(new Map());
      return data;
    }).catch((errors) => {
      const errorsMap = errors.inner.reduce((accum, item, index) => {
        const field = document.querySelector(`[name="${item.path}"]`);

        if (field && scrollToError) {
          const details = field.closest('details');

          if (details) {
            details.open = true;
          }

          if (index === 0) {
            field.closest('.signup-form-item').scrollIntoView({behavior: 'smooth'});
          }
        }

        accum.set(item.path, item.errors);

        return accum;
      }, new Map());

      setFormErrors(errorsMap);

      return false;
    });
  }, [state?.education]);

  const onSubmit = useCallback(e => {
    e.preventDefault();

    validate().then((valid) => {
      if (valid) {
        setFormErrors(new Map());
        history.push('/signup/step/3');
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    });

  }, [history, validate]);

  useEffect(() => {
    if (!state?.education?.length) {
      setState({
        ...state,
        education: [{}],
      });
    }
  }, [state, setState]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    validate(false, true);
  }, [state.education, validate]);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const message = params.get('message');

    if (message) {
      history.replace(history.location.pathname);
      action.popup.show(message);
    }
  }, [history]);

  return (
    <form onSubmit={onSubmit}>
      <SignupLayout
        title={'Step 2: Educational Background'}
        contentBefore={(
          <>
            Need help/suppport? Email
            {' '}
            <a href="mailto:support@gobuyside.com>">support@gobuyside.com</a>
          </>
        )}
        footer={
          <Button
            size={'xl'}
            icon={ArrowRightIcon}
            iconProps={{size: 21}}
            iconPosition={'right'}
          >
            Continue
          </Button>
        }
      >
        <Repeater
          items={items}
          renderItem={(item, index) => (
            <EducationItem
              index={index}
              education={item.data}
              degrees={degrees}
              isDegreesLoading={isDegreesLoading}
              gpa={gpa}
              isGpaLoading={isGpaLoading}
              errors={formErrors}
            />
          )}
          renderHeader={(item, index) => {
            if (index === 0) {
              return <span className={'education-item-header'}>Undergraduate Degree</span>;
            }

            return (
              <>
                {item.data.school ? (
                  <span className={'education-item-header'}>
                  {!!item.data.school.logo && (
                    <img width={22} height={22} src={item.data.school.logo} alt={item.data.school.name}/>
                  )}
                    {item.data.school.name}
                </span>
                ) : (
                  <span>Education {index + 1}</span>
                )}
              </>
            );
          }}
          onDelete={(index) => {
            setState({
              ...state,
              education: state.education.filter((_, i) => i !== index),
            });
          }}
          onAdd={() => {
            setState({
              ...state,
              education: [
                ...state.education,
                {},
              ],
            });
          }}
          addNewText={'Add Graduate Degree'}
        />
      </SignupLayout>
    </form>
  );
};

export default SignupStepTwo;
